import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useParams, matchPath } from "react-router-dom"
import { supabase, getCachedAnalyticsAvailability, markAnalyticsTableUnavailable, isNotFoundError, probeAnalyticsTable } from "@/lib/supabase"
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth"
import { useLocationState } from "@/contexts/LocationContext"
import { useLanguage } from "@/lib/lang"
import { getCanonicalModuleSlugFromPathname, getInstitutionId, getModulePageIdFromSlug, safeRandomUUID, stripLangPrefix } from "@/lib/utils"
import { UpdateToast } from "@/components/UpdateToast"
import { broadcastViewUpdate, useCounters } from "@/contexts/CountersContext"

const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

const DesktopLayout = React.lazy(() => import("./DesktopLayout"))
const MobileLayout = React.lazy(() => import("./MobileLayout"))

const TAB_ROUTES = [
  "/gp/menu",
  "/my-village-weather",
  "/online-services",
  "/eminent-persons",
  "/gp/rules",
  "/gp/apply",
  "/samvaad",
  "/reelgram",
  "/reelgram/*",
  "/my-music",
  "/village-tour",
  "/village-hub",
  "/voter-election",
  "/:lang/gp/menu",
  "/:lang/my-village-weather",
  "/:lang/online-services",
  "/:lang/farmer-services",
  "/:lang/eminent-persons",
  "/:lang/gp/rules",
  "/:lang/gp/apply",
  "/:lang/samvaad",
  "/:lang/reelgram",
  "/:lang/reelgram/*",
  "/:lang/my-music",
  "/:lang/village-tour",
  "/:lang/village-hub",
  "/:lang/voter-election",
  "/:lang/gram-samvaad"
]

let hasTrackedPageLoad = false
let lastTrackedModuleKey: string | null = null

const warnOnce = (key: string, message: string) => {
  try {
    const w = window as any
    w.__warnOnceKeys = w.__warnOnceKeys || {}
    if (w.__warnOnceKeys[key]) return
    w.__warnOnceKeys[key] = true
    console.warn(message)
  } catch {
    void 0
  }
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return
    const mql = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    setMatches(mql.matches)
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange)
      return () => mql.removeEventListener("change", onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [query])

  return matches
}

export default function MainLayout() {
  const location = useLocation()
  const { lang: paramsLang } = useParams<{ lang?: string }>()
  const { lang, setLang } = useLanguage()
  const { user } = useSupabaseAuth()
  const { selectedDistrict, selectedBlock, selectedVillage } = useLocationState()
  const { optimisticBumpView } = useCounters()

  useEffect(() => {
    const supported = ["en", "hi", "pa"] as const
    const next = supported.includes(paramsLang as any) ? (paramsLang as typeof supported[number]) : null
    if (next && next !== lang) {
      setLang(next)
    }
  }, [paramsLang, lang, setLang])

  useEffect(() => {
    if (hasTrackedPageLoad) return
    const trackView = async () => {
      const analyticsStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
      const storageKey = "device_hash"
      let deviceHash = ""
      try {
        deviceHash = localStorage.getItem(storageKey) || safeRandomUUID()
        localStorage.setItem(storageKey, deviceHash)
      } catch {
        deviceHash = safeRandomUUID()
      }

      const stripLang = (pathname: string) => {
        if (pathname.startsWith("/en/") || pathname.startsWith("/hi/") || pathname.startsWith("/pa/")) return pathname.slice(3)
        if (pathname === "/en" || pathname === "/hi" || pathname === "/pa") return "/"
        return pathname
      }

      const normalizedPath = stripLang(location.pathname)

      const pagePath = `${normalizedPath}${location.search || ""}`

      const normalize = (v: string | null | undefined) => (v ?? "").trim().toLowerCase()
      const sp = new URLSearchParams(location.search)
      const levelRaw = normalize(sp.get("level"))
      const district = sp.get("district") ?? selectedDistrict
      const block = sp.get("block") ?? selectedBlock
      const village = sp.get("village") ?? selectedVillage

      let level: "gp" | "block" | "zila" | null = null
      if (levelRaw === "gp") level = "gp"
      if (levelRaw === "bs" || levelRaw === "block") level = "block"
      if (levelRaw === "zp" || levelRaw === "zila") level = "zila"

      if (!level) {
        const d = normalize(district)
        const b = normalize(block)
        const v = normalize(village)
        if (v) level = "gp"
        else if (b) level = "block"
        else if (d) level = "zila"
      }

      const d = normalize(district)
      const b = normalize(block)
      const v = normalize(village)
      if (!level) {
        return
      }
      if (level === "gp" && (!d || !b || !v)) {
        return
      }
      if (level === "block" && (!d || !b)) {
        return
      }
      if (level === "zila" && !d) {
        return
      }

      const institutionId = getInstitutionId(level, district, block, village)
      if (!institutionId) {
        return
      }

      const pageType = "global"
      const pageId = "global"

      hasTrackedPageLoad = true
      // #region debug-point D:global-analytics-start
      reportPerfDebug("D", "src/components/layout/MainLayout.tsx:173", "[DEBUG] Global page-view analytics started", {
        pagePath,
        institutionId,
        level,
      })
      // #endregion
      let trackedOk = false
      try {
        if (!getCachedAnalyticsAvailability("page_views")) {
          await probeAnalyticsTable(supabase, "page_views")
        }
        const analyticsAvail = getCachedAnalyticsAvailability("page_views")
        if (analyticsAvail !== "available") {
          return
        }
        const { error } = await supabase.rpc("increment_page_view", {
          p_page_type: pageType,
          p_page_id: pageId,
          p_institution_id: institutionId,
          p_institution_type: level,
          p_viewer_user_id: user?.id || null,
          p_device_hash: deviceHash,
        })
        if (error) {
          if (isNotFoundError(error) || typeof (error as any)?.code === "string") {
            markAnalyticsTableUnavailable("page_views")
          }
          throw error
        }
        trackedOk = true
      } catch (error) {
        warnOnce("analytics:increment_page_view:global", "Analytics warning: increment_page_view failed (global)")
        return
      }

      const payload = {
        globalDelta: 1,
        institutionId,
        institutionDelta: 1,
      }
      // #region debug-point D:global-analytics-complete
      reportPerfDebug("D", "src/components/layout/MainLayout.tsx:210", "[DEBUG] Global page-view analytics completed", {
        institutionId,
        trackedOk,
        elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - analyticsStartMs,
      })
      // #endregion
      if (trackedOk) {
        optimisticBumpView(payload)
        broadcastViewUpdate(payload)
      }
    }

    void trackView().catch(() => warnOnce("analytics:increment_page_view:global:outer", "Analytics warning: increment_page_view failed (global)"))
  }, [location.pathname, location.search, selectedDistrict, selectedBlock, selectedVillage, user?.id])

  useEffect(() => {
    const trackModuleView = async () => {
      const analyticsStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
      const normalizedPath = stripLangPrefix(location.pathname)
      const key = `${normalizedPath}?${location.search}`
      if (lastTrackedModuleKey === key) return

      const moduleName = getCanonicalModuleSlugFromPathname(normalizedPath)
      if (!moduleName) return

      const storageKey = "device_hash"
      let deviceHash = ""
      try {
        deviceHash = localStorage.getItem(storageKey) || safeRandomUUID()
        localStorage.setItem(storageKey, deviceHash)
      } catch {
        deviceHash = safeRandomUUID()
      }

      const normalize = (v: string | null | undefined) => (v ?? "").trim().toLowerCase()
      const sp = new URLSearchParams(location.search)
      const levelRaw = normalize(sp.get("level"))
      const district = sp.get("district") ?? selectedDistrict
      const block = sp.get("block") ?? selectedBlock
      const village = sp.get("village") ?? selectedVillage

      let level: "gp" | "block" | "zila" | null = null
      if (levelRaw === "gp") level = "gp"
      if (levelRaw === "bs" || levelRaw === "block") level = "block"
      if (levelRaw === "zp" || levelRaw === "zila") level = "zila"

      if (!level) {
        const d = normalize(district)
        const b = normalize(block)
        const v = normalize(village)
        if (v) level = "gp"
        else if (b) level = "block"
        else if (d) level = "zila"
      }

      const d = normalize(district)
      const b = normalize(block)
      const v = normalize(village)
      if (!level) {
        return
      }
      if (level === "gp" && (!d || !b || !v)) {
        return
      }
      if (level === "block" && (!d || !b)) {
        return
      }
      if (level === "zila" && !d) {
        return
      }

      const institutionId = getInstitutionId(level, district, block, village)
      if (!institutionId) {
        return
      }

      const pageType = "module"
      const pageId = getModulePageIdFromSlug(moduleName)
      if (!pageId) return
      const trackingKey = `${pageType}:${pageId}:${institutionId}:${key}`
      if ((window as any).__tracking_done === trackingKey) return
        ; (window as any).__tracking_done = trackingKey

      lastTrackedModuleKey = key
      // #region debug-point D:module-analytics-start
      reportPerfDebug("D", "src/components/layout/MainLayout.tsx:287", "[DEBUG] Module page-view analytics started", {
        moduleName,
        pageId,
        institutionId,
      })
      // #endregion
      let trackedOk = false
      try {
        if (!getCachedAnalyticsAvailability("page_views")) {
          await probeAnalyticsTable(supabase, "page_views")
        }
        const analyticsAvail = getCachedAnalyticsAvailability("page_views")
        if (analyticsAvail !== "available") {
          return
        }
        const { error } = await supabase.rpc("increment_page_view", {
          p_page_type: pageType,
          p_page_id: pageId,
          p_institution_id: institutionId,
          p_institution_type: level,
          p_viewer_user_id: user?.id || null,
          p_device_hash: deviceHash,
        })
        if (error) {
          if (isNotFoundError(error) || typeof (error as any)?.code === "string") {
            markAnalyticsTableUnavailable("page_views")
          }
          throw error
        }
        trackedOk = true
      } catch (error) {
        warnOnce("analytics:increment_page_view:module", "Analytics warning: increment_page_view failed (module)")
        return
      }

      const payload = {
        globalDelta: 1,
        institutionId,
        institutionDelta: 1,
        modulePageId: pageId,
        moduleDelta: 1,
      }
      // #region debug-point D:module-analytics-complete
      reportPerfDebug("D", "src/components/layout/MainLayout.tsx:329", "[DEBUG] Module page-view analytics completed", {
        moduleName,
        pageId,
        institutionId,
        trackedOk,
        elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - analyticsStartMs,
      })
      // #endregion
      if (trackedOk) {
        optimisticBumpView(payload)
        broadcastViewUpdate(payload)
      }
    }

    void trackModuleView().catch(() => warnOnce("analytics:increment_page_view:module:outer", "Analytics warning: increment_page_view failed (module)"))
  }, [location.pathname, location.search, selectedDistrict, selectedBlock, selectedVillage, user?.id])

  const isTabRoute = useMemo(() => TAB_ROUTES.some(route =>
    matchPath({ path: route, end: false }, location.pathname)
  ), [location.pathname])

  const isDesktop = useMediaQuery("(min-width: 1025px)")

  if (isDesktop) {
    return (
      <>
        <React.Suspense fallback={null}>
          <DesktopLayout showFooter={!isTabRoute} />
        </React.Suspense>
        <UpdateToast />
      </>
    )
  }

  return (
    <>
      <React.Suspense fallback={null}>
        <MobileLayout />
      </React.Suspense>
      <UpdateToast />
    </>
  )
}
